import dayjs from "dayjs";
import type { CurrencyResource } from "@exonn/js-sdk";

export function formatPrice(
  x: number,
  currency?: {
    symbol: string;
  },
) {
  return (
    x.toLocaleString("de-DE", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }) + (currency ? ` ${currency.symbol}` : "")
  );
}

export function formatPriceV2(x: number, currency?: CurrencyResource) {
  return (
    x.toLocaleString("de-DE", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    }) + (currency ? ` ${currency.iso}` : "")
  );
}

export function formatWeight(
  value: number,
  unit?: {
    title: string;
  } | null,
) {
  const translations: Record<string, string> = {
    l: "L",
  };

  const ending = unit ? " " + (translations?.[unit.title] || unit.title) : "";

  return toFixedWithoutZeros(value, 6) + ending;
}

export function formatPercents(value: number) {
  return toFixedWithoutZeros(value, 6) + "%";
}

export function formatDate(value: string) {
  return dayjs(value).format("DD.MM.YYYY");
}

export function formatDateTime(value: string) {
  // default timezone is set in App.tsx
  return dayjs(value).format("DD.MM.YYYY HH:mm");
}

/** Пояс, в котором API отдаёт время без смещения (AppServiceProvider). */
const APP_TIME_ZONE = "Europe/Berlin";

const NAIVE_DATE_TIME =
  /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/;

const berlinParts = (instant: Date) =>
  Object.fromEntries(
    new Intl.DateTimeFormat("de-DE", {
      timeZone: APP_TIME_ZONE,
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
      timeZoneName: "short",
    })
      .formatToParts(instant)
      .map((part) => [part.type, part.value]),
  );

/**
 * Время по Берлину с названием пояса («14.10.2026 09:05 MESZ»): так коллеги
 * в разных поясах видят одну и ту же отправку одинаково.
 *
 * Строка без смещения — это уже берлинское время (так API отдаёт даты
 * моделей): её цифры не пересчитываются, иначе браузер в другом поясе
 * сдвинул бы их. Строка со смещением переводится в Берлин.
 */
export function formatDateTimeInZone(value: string) {
  const naive = NAIVE_DATE_TIME.exec(value);

  if (naive) {
    const [, year, month, day, hour, minute] = naive;
    // Название пояса — на этот момент; смещение Берлина зимой +1, летом +2,
    // час разницы для выбора MEZ/MESZ значения не имеет.
    const zone = berlinParts(
      new Date(Date.UTC(+year, +month - 1, +day, +hour - 1, +minute)),
    ).timeZoneName;

    return `${day}.${month}.${year} ${hour}:${minute} ${zone}`;
  }

  const parts = berlinParts(new Date(value));

  return `${parts.day}.${parts.month}.${parts.year} ${parts.hour}:${parts.minute} ${parts.timeZoneName}`;
}

export const toFixedWithoutZeros = (
  num: number,
  precision: number,
  decimalSeparator: string = ".",
) => {
  return `${Number.parseFloat(num.toFixed(precision))}`.replace(
    ".",
    decimalSeparator,
  );
};

export function formatPercent(x: number) {
  return x.toLocaleString("de-DE", {
    maximumFractionDigits: 6,
    minimumFractionDigits: 0,
  });
}

export const formatToLocalPhone = (phone: string, callingCode: string) => {
  if (!callingCode) {
    return phone;
  }
  const normalized = phone.replaceAll("+", "");
  if (!normalized.startsWith(callingCode)) return phone;
  return "0" + normalized.slice(callingCode.length);
};

export const formatNumber = (number: number): string => {
  const formatter = new Intl.NumberFormat("fr-FR");

  return formatter.format(number); // "1 234 567"
};
